/* Created by Interactive 63 - https://interactive63.com */
/**
 * Saved easings: named easings kept in localStorage as a per-browser convenience. They are not
 * part of any project. Every read and write is guarded because storage can be blocked or full.
 */
import { evaluateEasing } from '../core/easing/evaluate';
import type { Easing } from '../core/model/types';
import { readStorage, writeStorage } from './storage';

export const SAVED_EASINGS_KEY = 'svg-tempo:easings';
export const MAX_SAVED_EASINGS = 30;

export interface SavedEasing {
  name: string;
  easing: Easing;
}

/** Whether a value is an easing the sampler can evaluate. */
function isEasing(value: unknown): value is Easing {
  if (typeof value !== 'object' || value === null) return false;
  try {
    return Number.isFinite(evaluateEasing(value as Easing, 0.5));
  } catch {
    return false;
  }
}

/** Reads the saved easings, dropping anything malformed. */
export function loadSavedEasings(): SavedEasing[] {
  const raw = readStorage(SAVED_EASINGS_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const saved: SavedEasing[] = [];
    for (const item of parsed as unknown[]) {
      if (typeof item !== 'object' || item === null) continue;
      const { name, easing } = item as Record<string, unknown>;
      if (typeof name === 'string' && name.trim() !== '' && isEasing(easing)) {
        saved.push({ name: name.trim().slice(0, 40), easing });
      }
    }
    return saved.slice(0, MAX_SAVED_EASINGS);
  } catch {
    return [];
  }
}

/** Saves an easing under a name, replacing one with the same name. Returns the new list. */
export function saveEasing(name: string, easing: Easing): SavedEasing[] {
  const trimmed = name.trim().slice(0, 40);
  if (trimmed === '') return loadSavedEasings();
  const others = loadSavedEasings().filter((s) => s.name !== trimmed);
  const next = [...others, { name: trimmed, easing }].slice(-MAX_SAVED_EASINGS);
  writeStorage(SAVED_EASINGS_KEY, JSON.stringify(next));
  return next;
}

/** Removes a saved easing by name. Returns the new list. */
export function removeSavedEasing(name: string): SavedEasing[] {
  const next = loadSavedEasings().filter((s) => s.name !== name);
  writeStorage(SAVED_EASINGS_KEY, JSON.stringify(next));
  return next;
}
